import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase/client'
import { ndtFormPath, setNdtExitPath } from '@/utils/ndtNavigation'

// NDT type selection page
const NDTSelection = () => {
  const navigate = useNavigate()
  const [ndtIcons, setNdtIcons] = useState<Array<{ name: string; url: string }>>([])
  const [userQualifications, setUserQualifications] = useState<Set<string>>(new Set())
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    const loadIconsAndQualifications = async () => {
      // Import all NDT icons from the ndt-icons folder first
      const icons = import.meta.glob('/src/assets/icons/ndt-icons/*.svg', { eager: true, as: 'url' })
      
      // Convert to array with proper naming
      const hiddenIcons = new Set(['Trykktest'])
      const iconData = Object.entries(icons)
        .map(([path, url]) => {
          const fileName = path.split('/').pop()?.replace('.svg', '') || ''
          return {
            name: fileName,
            url: url as string
          }
        })
        .filter((icon) => !hiddenIcons.has(icon.name) && !/[_-]/.test(icon.name))
      
      // Sort icons: ET first, then MPI, then alphabetically
      iconData.sort((a, b) => {
        if (a.name === 'ET') return -1
        if (b.name === 'ET') return 1
        if (a.name === 'MPI') return -1
        if (b.name === 'MPI') return 1
        return a.name.localeCompare(b.name)
      })
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        setNdtIcons(iconData)
        return
      }
      
      // Get user profile with qualifications
      // Note: Database has both boolean columns (et, mt, pt, etc.) and level columns (et_level, mt_level, etc.)
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('role, supervisor, et, mt, pt, vt, ut, rt, lt, thread, et_level, mt_level, pt_level, vt_level, ut_level, rt_level, lt_level, thread_level')
        .eq('id', user.id)
        .single()
      
      if (error) {
        console.error('Error loading profile:', error)
        // Don't show any icons if we can't load qualifications
        // This is safer than showing all icons
        setNdtIcons([])
        return
      }
      
      if (!profile) {
        console.error('No profile found for user')
        setNdtIcons([])
        return
      }
      
      const isUserAdmin = profile?.role === 'admin'
      const isUserSupervisor = profile?.supervisor === true
      const hasFullAccess = isUserAdmin || isUserSupervisor
      
      setIsAdmin(hasFullAccess)
      
      // If admin or supervisor, show all icons
      if (hasFullAccess) {
        console.log(isUserAdmin ? 'Admin user - showing all NDT icons' : 'Supervisor user - showing all NDT icons')
        setNdtIcons(iconData)
        return
      }
      
      // Build set of qualified NDT methods for inspector
      const qualifications = new Set<string>()
      
      console.log('Profile data:', {
        // Boolean qualifications
        et: profile.et,
        mt: profile.mt,
        pt: profile.pt,
        vt: profile.vt,
        ut: profile.ut,
        rt: profile.rt,
        lt: profile.lt,
        thread: profile.thread,
        // Level qualifications
        et_level: profile.et_level,
        mt_level: profile.mt_level,
        pt_level: profile.pt_level,
        vt_level: profile.vt_level,
        ut_level: profile.ut_level,
        rt_level: profile.rt_level,
        lt_level: profile.lt_level,
        thread_level: profile.thread_level
      })
      
      // Map database fields to icon names
      // Check both boolean columns (et, mt, etc.) and level columns (et_level, mt_level, etc.)
      // Boolean column takes precedence: if explicitly false, NOT qualified regardless of level
      // If boolean is null/undefined, check level (any level 1-3 is qualified)
      if (profile.et === true || (profile.et !== false && profile.et_level && profile.et_level > 0)) qualifications.add('ET')
      if (profile.mt === true || (profile.mt !== false && profile.mt_level && profile.mt_level > 0)) qualifications.add('MPI')
      if (profile.pt === true || (profile.pt !== false && profile.pt_level && profile.pt_level > 0)) qualifications.add('PT')
      if (profile.vt === true || (profile.vt !== false && profile.vt_level && profile.vt_level > 0)) qualifications.add('VT')
      if (profile.ut === true || (profile.ut !== false && profile.ut_level && profile.ut_level > 0)) qualifications.add('UT')
      if (profile.rt === true || (profile.rt !== false && profile.rt_level && profile.rt_level > 0)) qualifications.add('RT')
      if (profile.lt === true || (profile.lt !== false && profile.lt_level && profile.lt_level > 0)) qualifications.add('LT')
      if (profile.thread === true || (profile.thread !== false && profile.thread_level && profile.thread_level > 0)) qualifications.add('Thread')
      
      setUserQualifications(qualifications)
      
      // Filter icons based on qualifications
      const filteredIcons = iconData.filter(icon => qualifications.has(icon.name))
      
      console.log('Inspector user - qualifications:', Array.from(qualifications))
      console.log('Available icons:', iconData.map(i => i.name))
      console.log('Filtered NDT icons:', filteredIcons.map(i => i.name))
      
      if (filteredIcons.length === 0) {
        console.warn('No NDT qualifications found for this inspector. User will see "Ingen NDT-kvalifikasjoner registrert"')
      }
      
      setNdtIcons(filteredIcons)
    }
    
    loadIconsAndQualifications()
  }, [])

  const handleSelection = (iconName: string) => {
    console.log('Selected NDT type:', iconName)
    
    sessionStorage.setItem('inspectionType', 'ndt')
    sessionStorage.setItem('ndtType', iconName)
    setNdtExitPath('/ndt-selection')

    const path = ndtFormPath(iconName)
    navigate(path ?? '/company')
  }

  return (
    <div className="min-h-full p-4 sm:p-8" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-6xl mx-auto">
        {/* Options Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-8 max-w-5xl mx-auto px-2 sm:px-0">
          {ndtIcons.map((icon, index) => (
            <button
              key={index}
              onClick={() => handleSelection(icon.name)}
              className="flex flex-col items-center justify-center p-4 sm:p-8 bg-white border-2 border-stone-300 rounded-2xl sm:rounded-3xl hover:border-[#1E3A5F] hover:shadow-lg transition-all active:scale-95 aspect-square group"
            >
              <img src={icon.url} alt={icon.name} className="w-full h-full object-contain" />
            </button>
          ))}
        </div>

        {ndtIcons.length === 0 && (
          <div className="text-center text-stone-500 text-lg sm:text-xl mt-8 sm:mt-12 px-4">
            Ingen NDT-kvalifikasjoner registrert
          </div>
        )}
      </div>
    </div>
  )
}

export default NDTSelection

